import { useEffect, useMemo, useState } from "react";
import { Flame, Loader2, Target } from "lucide-react";
import CategoryFilter from "../../donation/CategoryFilter";
import CampaignCard from "../../donation/CampaignCard";
import { schemesToCampaigns } from "../../../donation/mappers";
import type {
  DonationCampaign,
  DonationCategoryFilter,
  DonationMainCategoryOption,
} from "../../../donation/types";
import {
  donationMainCategoryAPI,
  donationSchemesAPI,
} from "../../../services/api";
import committeeBG from "../../../assets/donatio-bg.jpg";

export default function DonationPage() {
  const [activeFilter, setActiveFilter] =
    useState<DonationCategoryFilter>("all");
  const [campaigns, setCampaigns] = useState<DonationCampaign[]>([]);
  const [categories, setCategories] = useState<DonationMainCategoryOption[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [categoriesResult, schemesResult] = await Promise.allSettled([
          donationMainCategoryAPI.list(),
          donationSchemesAPI.list({ page_size: 1000, is_active: true }),
        ]);

        if (cancelled) {
          return;
        }

        if (schemesResult.status === "rejected") {
          throw schemesResult.reason;
        }

        const categoryList =
          categoriesResult.status === "fulfilled" ? categoriesResult.value : [];
        if (categoriesResult.status === "rejected") {
          console.warn(
            "Failed to load donation categories:",
            categoriesResult.reason
          );
        }

        const mappedCampaigns = schemesToCampaigns(schemesResult.value.schemes);
        let categoryOptions: DonationMainCategoryOption[] = categoryList.map(
          c => ({
            id: String(c.id),
            name: c.name,
          })
        );
        if (categoryOptions.length === 0 && mappedCampaigns.length > 0) {
          const seen = new Map<string, string>();
          for (const c of mappedCampaigns) {
            if (c.categoryId && !seen.has(c.categoryId)) {
              seen.set(c.categoryId, c.categoryName);
            }
          }
          categoryOptions = [...seen.entries()].map(([id, name]) => ({
            id,
            name,
          }));
        }
        setCategories(categoryOptions);
        setCampaigns(mappedCampaigns);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load donation campaigns:", err);
          setError("Unable to load campaigns. Please try again later.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // const stats = useMemo(() => getAggregateStats(campaigns), [campaigns]);
  // const overallProgress = getFundingProgress(
  //   stats.totalRaised,
  //   stats.totalGoal
  // );

  const filtered = useMemo(() => {
    if (activeFilter === "all") {
      return campaigns;
    }
    return campaigns.filter(c => c.categoryId === activeFilter);
  }, [campaigns, activeFilter]);

  const ongoingCampaigns = useMemo(
    () =>
      filtered.filter(
        c =>
          c.featured &&
          c.goal > 0 &&
          (c.dateStatus === "active" ||
            c.dateStatus === "live" ||
            c.dateStatus === "upcoming")
      ),
    [filtered]
  );

  const campaignsByCategory = useMemo(() => {
    const source =
      activeFilter === "all"
        ? campaigns
        : campaigns.filter(c => c.categoryId === activeFilter);

    const categoryOrder =
      activeFilter === "all"
        ? categories
        : categories.filter(c => c.id === activeFilter);

    const groups: {
      category: DonationMainCategoryOption;
      items: DonationCampaign[];
    }[] = [];

    for (const cat of categoryOrder) {
      const items = source.filter(c => c.categoryId === cat.id);
      if (items.length > 0) {
        groups.push({ category: cat, items });
      }
    }

    const knownIds = new Set(categoryOrder.map(c => c.id));
    const uncategorized = source.filter(c => !knownIds.has(c.categoryId));
    if (uncategorized.length > 0) {
      groups.push({
        category: { id: "other", name: "Other" },
        items: uncategorized,
      });
    }

    return groups;
  }, [campaigns, categories, activeFilter]);

  const showOngoingSection =
    activeFilter === "all" && ongoingCampaigns.length > 0;

  return (
    <div className="min-h-screen bg-[#FFF9F0]">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#7A1E1E] text-white">
        <div className="absolute inset-0 opacity-40">
          <img
            src={committeeBG}
            alt=""
            className="h-full w-full object-cover"
            aria-hidden
          />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#D4A017]/40 bg-[#5A1515]/50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#D4A017]">
                <Flame className="h-4 w-4" />
                Live donations
              </span>
              <h1 className="mb-4 text-4xl font-bold leading-tight lg:text-5xl">
                Your kindness keeps our community thriving.
              </h1>
              <p className="mb-8 max-w-xl text-lg text-white/90">
                Pick a campaign that moves your heart — from one-day meal seva
                to sponsoring infrastructure. Every contribution is acknowledged
                with an 80G receipt.
              </p>
              {/* <a
                href="#campaigns"
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#B8900F] px-6 py-3 font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
              >
                <Heart className="h-5 w-5" />
                Browse Campaigns
              </a> */}
            </div>

            {/* <DonationStatsCard
              totalRaised={stats.totalRaised}
              totalGoal={stats.totalGoal}
              progressPercent={overallProgress}
              activeDrives={stats.activeDrives || campaigns.length}
            /> */}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[5rem] z-20 border-b border-gray-100 bg-[#FFF9F0]/95 backdrop-blur-md sm:top-[8.9rem]">
        <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 lg:px-8">
          <CategoryFilter
            categories={categories}
            active={activeFilter}
            onChange={setActiveFilter}
          />
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="h-10 w-10 animate-spin text-[#7A1E1E]" />
            <p className="mt-4 text-gray-600">Loading campaigns...</p>
          </div>
        ) : error ? (
          <p className="py-12 text-center text-red-600">{error}</p>
        ) : (
          <>
            {showOngoingSection && (
              <Section
                id="campaigns"
                icon={Target}
                title="Ongoing Campaigns"
                subtitle="Help us reach the goal — every contribution moves the needle."
              >
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {ongoingCampaigns.map(c => (
                    <CampaignCard key={c.id} campaign={c} />
                  ))}
                </div>
              </Section>
            )}

            {campaignsByCategory.map(({ category, items }) => {
              const ongoingIds = new Set(ongoingCampaigns.map(c => c.id));
              const sectionItems =
                showOngoingSection && activeFilter === "all"
                  ? items.filter(c => !ongoingIds.has(c.id))
                  : items;
              if (sectionItems.length === 0) {
                return null;
              }

              return (
                <Section
                  key={category.id}
                  icon={Target}
                  title={category.name}
                  subtitle={
                    activeFilter === "all"
                      ? "Browse campaigns in this category."
                      : `All active campaigns under ${category.name}.`
                  }
                >
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {sectionItems.map(c => (
                      <CampaignCard
                        key={c.id}
                        campaign={c}
                        variant="category"
                      />
                    ))}
                  </div>
                </Section>
              );
            })}

            {!loading && filtered.length === 0 && (
              <p className="py-12 text-center text-gray-500">
                No campaigns in this category yet.
              </p>
            )}
          </>
        )}

        <TrustSection />
      </div>
    </div>
  );
}

function Section({
  id,
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  id?: string;
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mb-14">
      <div className="mb-6 flex items-start gap-3">
        {Icon && (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#7A1E1E]/10 text-[#7A1E1E]">
            <Icon className="h-5 w-5" />
          </div>
        )}
        <div>
          <h2 className="text-2xl font-bold text-[#7A1E1E] lg:text-3xl">
            {title}
          </h2>
          <p className="mt-1 text-gray-600">{subtitle}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function TrustSection() {
  const items = [
    {
      title: "100% transparent",
      text: "Every donation is tracked and acknowledged with a digital receipt.",
    },
    {
      title: "80G tax benefit",
      text: "Official 80G receipt issued within 24 hours of confirmed payment.",
    },
    {
      title: "Trusted community",
      text: "Backed by trustees, volunteers and generous donors across India.",
    },
  ];

  return (
    <section className="mt-8 grid gap-6 border-t border-gray-200 pt-12 sm:grid-cols-3">
      {items.map(item => (
        <div key={item.title} className="text-center sm:text-left">
          <h3 className="mb-2 font-semibold text-[#7A1E1E]">{item.title}</h3>
          <p className="text-sm text-gray-600">{item.text}</p>
        </div>
      ))}
    </section>
  );
}
